import { useCallback, useEffect, useMemo, useState } from 'react';

import { useRouteLoaderData } from 'react-router-dom';

import { InspectionLoaderData } from 'components/inspections/types';
import { MapAnomalies } from 'components/Sites/types';
import { DIFFERENTIAL_MODES } from 'lib/constants';
import {
    buildDifferentialComparisonContextAsync,
    checkIfFallbackNeeded,
    createAnomalyDefinitionMap,
    filterDifferentialAnomaliesAsync,
    filterDifferentialAnomaliesUUIDOnly,
    getTotalAnomalyCount,
    transformRawAnomalies,
} from 'lib/helpers';
import createCustomHook from 'lib/helpers/hookFactory';
import { GET_REPORT_ANOMALIES, GET_REPORT_ANOMALY_SUMMARY } from 'lib/queries';
import { client } from 'lib/urqlClient';

import { useAnomalyMapData } from './useAnomalyMapData';
import { useDifferentialMode } from '../useDifferentialMode';

const ValueProvider = () => {
    const [previousInspectionMapAnomalies, setPreviousInspectionMapAnomalies] = useState<MapAnomalies>({});
    const [differentialMapAnomalies, setDifferentialMapAnomalies] = useState<any>({});
    const [isLoadingPreviousData, setIsLoadingPreviousData] = useState(false);
    const [isComputingDifferential, setIsComputingDifferential] = useState(false);
    const [isUsingFallbackMatching, setIsUsingFallbackMatching] = useState(false);

    const { currentInspectionMapAnomalies, setMapAnomalies } = useAnomalyMapData();
    const { active: isDifferentialModeActive, mode } = useDifferentialMode();
    const currentInspectionData = useRouteLoaderData('inspection') as InspectionLoaderData;

    useEffect(() => {
        const fetchPreviousInspectionData = async () => {
            const previousInspection = currentInspectionData?.previousInspection;

            if (!previousInspection) {
                return;
            }

            setIsLoadingPreviousData(true);
            try {
                const previousInspectionUUID = previousInspection.inspectionID;
                const previousInspectionSATUUIDs = previousInspection.superAnalysisTasks.map(
                    (sat: any) => sat.superAnalysisTaskUUID,
                );

                const reportAnomaliesPromise = client
                    .query(GET_REPORT_ANOMALIES, {
                        superAnalysisTaskUUIDs: previousInspectionSATUUIDs,
                        // Always include asset region data for differential comparison
                        // This enables fallback matching by position and nomenclature
                        // when inspections use different draft versions
                        includeAssetRegion: true,
                    })
                    .toPromise();

                const reportAnomalySummaryPromise = client
                    .query(GET_REPORT_ANOMALY_SUMMARY, {
                        uuid: previousInspectionUUID,
                    })
                    .toPromise();

                const [{ data: previousReportAnomalyData }, { data: previousReportAnomalySummary }] = await Promise.all([
                    reportAnomaliesPromise,
                    reportAnomalySummaryPromise,
                ]);

                setPreviousInspectionMapAnomalies(
                    transformRawAnomalies(
                        previousReportAnomalyData,
                        null,
                        createAnomalyDefinitionMap(previousReportAnomalySummary?.anomalySummary),
                    ),
                );
            } catch (error) {
                console.error('Error fetching previous inspection data for differential comparison:', error);
            } finally {
                setIsLoadingPreviousData(false);
            }
        };

        if (isDifferentialModeActive) {
            fetchPreviousInspectionData();
        }
    }, [currentInspectionData?.previousInspection, isDifferentialModeActive]);

    useEffect(() => {
        if (isDifferentialModeActive && differentialMapAnomalies[mode]) {
            setMapAnomalies(differentialMapAnomalies[mode].anomalies);
        } else {
            setMapAnomalies(currentInspectionMapAnomalies);
        }
    }, [isDifferentialModeActive, differentialMapAnomalies, mode, setMapAnomalies, currentInspectionMapAnomalies]);

    // Compute all differential modes once when context is ready
    // Uses lazy fallback: tries fast UUID-only matching first,
    // only falls back to position/nomenclature matching if needed
    useEffect(() => {
        if (
            Object.keys(currentInspectionMapAnomalies).length === 0 ||
            Object.keys(previousInspectionMapAnomalies).length === 0
        ) {
            setDifferentialMapAnomalies({});
            setIsComputingDifferential(false);
            setIsUsingFallbackMatching(false);
            return;
        }

        setIsComputingDifferential(true);
        setIsUsingFallbackMatching(false);
        
        // Use a shared reference object instead of a local variable
        // This ensures the async function always sees the latest cancellation state,
        // even when the effect re-runs while a previous async operation is in progress
        const cancelRef = { cancelled: false };

        const computeAsync = async () => {
            try {
                const modes = Object.values(DIFFERENTIAL_MODES);
                const results: any = {};

                // PHASE 1: Fast UUID-only matching (O(n))
                for (const modeValue of modes) {
                    if (cancelRef.cancelled) return;

                    const filteredDifferentialAnomalies = filterDifferentialAnomaliesUUIDOnly(
                        currentInspectionMapAnomalies,
                        previousInspectionMapAnomalies,
                        modeValue,
                    );

                    results[modeValue] = {
                        anomalies: filteredDifferentialAnomalies,
                        count: getTotalAnomalyCount(filteredDifferentialAnomalies),
                    };
                }

                if (cancelRef.cancelled) return;

                // PHASE 2: Check if fallback is needed
                // Fallback is needed when UUID matching finds no overlapping anomalies,
                // which indicates different draft versions with different asset_region_uuids
                const needsFallback = checkIfFallbackNeeded(
                    currentInspectionMapAnomalies,
                    previousInspectionMapAnomalies,
                );

                if (needsFallback) {
                    console.log(
                        '[Differential Comparison] Fallback matching required - detected different draft versions. ' +
                        'UUID matching found 0 overlapping anomalies. Using position/nomenclature matching.',
                    );
                    setIsUsingFallbackMatching(true);

                    // Build context with fallback maps (expensive, but only when needed)
                    const context = await buildDifferentialComparisonContextAsync(
                        currentInspectionMapAnomalies,
                        previousInspectionMapAnomalies,
                    );

                    if (cancelRef.cancelled) return;

                    // Re-process with full fallback matching
                    for (const modeValue of modes) {
                        if (cancelRef.cancelled) return;

                        const filteredDifferentialAnomalies = await filterDifferentialAnomaliesAsync(
                            currentInspectionMapAnomalies,
                            previousInspectionMapAnomalies,
                            modeValue,
                            context,
                        );

                        if (cancelRef.cancelled) return;

                        results[modeValue] = {
                            anomalies: filteredDifferentialAnomalies,
                            count: getTotalAnomalyCount(filteredDifferentialAnomalies),
                        };
                    }
                } else {
                    console.log(
                        '[Differential Comparison] UUID matching successful - using fast path.',
                    );
                }

                if (!cancelRef.cancelled) {
                    setDifferentialMapAnomalies(results);
                }
            } catch (error) {
                console.error('Error computing differential anomalies:', error);
                if (!cancelRef.cancelled) {
                    setDifferentialMapAnomalies({});
                }
            } finally {
                if (!cancelRef.cancelled) {
                    setIsComputingDifferential(false);
                }
            }
        };

        computeAsync();

        return () => {
            cancelRef.cancelled = true;
        };
    }, [currentInspectionMapAnomalies, previousInspectionMapAnomalies]);

    const getAnomalyCountForDifferentialMode = useCallback(
        (mode: DIFFERENTIAL_MODES) => differentialMapAnomalies[mode]?.count ?? 0,
        [differentialMapAnomalies],
    );

    // Combined loading state for differential comparison
    const isLoadingDifferentialData = isLoadingPreviousData || isComputingDifferential;

    const value = useMemo(
        () => ({
            previousRawMapAnomalies: previousInspectionMapAnomalies,
            previousInspectionId: currentInspectionData?.previousInspection?.inspectionID,
            getAnomalyCountForDifferentialMode,
            isLoadingDifferentialData,
            isUsingFallbackMatching,
        }),
        [
            previousInspectionMapAnomalies,
            currentInspectionData?.previousInspection?.inspectionID,
            getAnomalyCountForDifferentialMode,
            isLoadingDifferentialData,
            isUsingFallbackMatching,
        ],
    );

    return value;
};

const { Provider: DifferentialAnomalyDataProvider, useCustomHook: useDifferentialAnomalyData } = createCustomHook<any>({
    name: 'useDifferentialAnomalyData',
    callback: ValueProvider,
});

export { DifferentialAnomalyDataProvider, useDifferentialAnomalyData };
